import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, Plus, Search, Layers, Edit3, Trash2, BookOpen 
} from 'lucide-react';

const SemesterManagement = () => {
  const [semesters, setSemesters] = useState([]);
  const [formData, setFormData] = useState({
    sem_no: '',
    sem_name: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [editingSemester, setEditingSemester] = useState(null);

  useEffect(() => {
    fetchSemesters();
  }, []);

  const fetchSemesters = async () => {
    try {
      const res = await axios.get('/api/semesters');
      setSemesters(res.data);
    } catch (err) {
      console.error('Failed to load semesters', err);
      setMessage({ text: 'Failed to load semesters', type: 'error' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Extra validation for sem_no
    if (name === 'sem_no') {
      const num = parseInt(value, 10);
      if (value === '' || (num >= 1 && num <= 8)) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const semNo = parseInt(formData.sem_no, 10);
    if (!formData.sem_no || isNaN(semNo) || semNo < 1 || semNo > 8) {
      setMessage({ text: 'Semester number must be between 1 and 8', type: 'error' });
      return;
    }
    if (!formData.sem_name.trim()) {
      setMessage({ text: 'Please enter semester name', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      if (editingSemester) {
        await axios.put(`/api/semesters/${editingSemester.sem_id}`, formData);
        setMessage({ text: 'Semester updated successfully!', type: 'success' });
      } else {
        await axios.post('/api/semesters', formData);
        setMessage({ text: 'Semester created successfully!', type: 'success' });
      }
      setFormData({ sem_no: '', sem_name: '' });
      setEditingSemester(null);
      fetchSemesters();
    } catch (err) {
      console.error(err);
      setMessage({
        text: err.response?.data?.error || 'Failed to save semester',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sem) => {
    setEditingSemester(sem);
    setFormData({
      sem_no: sem.sem_no,
      sem_name: sem.sem_name,
    });
  };

  const handleDelete = async (semId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Are you sure you want to delete this semester?')) return;

    try {
      await axios.delete(`/api/semesters/${semId}`);
      setMessage({ text: 'Semester deleted successfully!', type: 'success' });
      fetchSemesters();
    } catch (err) {
      setMessage({ text: 'Failed to delete semester', type: 'error' });
    }
  };

  const filteredSemesters = semesters.filter((sem) =>
    sem.sem_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(sem.sem_no).includes(searchTerm)
  );

  return (
    <div style={styles.container}>
      <div style={styles.contentWrapper}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconBg}>
              <Calendar size={28} style={styles.headerIcon} />
            </div>
            <div>
              <h1 style={styles.title}>Semester Management</h1>
              <p style={styles.subtitle}>Manage academic semesters (1 to 8)</p>
            </div>
          </div>
          <button
            style={styles.newBtn}
            onClick={() => {
              setEditingSemester(null);
              setFormData({ sem_no: '', sem_name: '' });
            }}
          >
            <Plus size={20} />
            {editingSemester ? 'Cancel Edit' : 'New Semester'}
          </button>
        </header>

        {/* Form Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>
              <Layers size={24} />
              {editingSemester ? 'Edit Semester' : 'Add New Semester'}
            </h2>
          </div>

          {message.text && (
            <div
              style={{
                ...styles.message,
                ...(message.type === 'success' ? styles.successMsg : styles.errorMsg),
              }}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Semester No <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  name="sem_no"
                  min="1"
                  max="8"
                  value={formData.sem_no}
                  onChange={handleInputChange}
                  placeholder="1 to 8"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Semester Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="sem_name"
                  value={formData.sem_name}
                  onChange={handleInputChange}
                  placeholder="e.g. Semester 1, Sem I, First Semester"
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <div style={styles.formActions}>
              <button
                type="button"
                onClick={() => {
                  setEditingSemester(null);
                  setFormData({ sem_no: '', sem_name: '' });
                }}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.submitBtn,
                  ...(loading && styles.submitBtnDisabled),
                }}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner} />
                    Saving...
                  </>
                ) : editingSemester ? (
                  'Update Semester'
                ) : (
                  'Create Semester'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Semesters Table */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>
              <BookOpen size={24} />
              Semesters List ({filteredSemesters.length})
            </h2>
            <div style={styles.searchContainer}>
              <Search size={20} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search semesters..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>
          </div>

          <div style={styles.tableContainer}>
            {filteredSemesters.length === 0 ? (
              <div style={styles.emptyState}>
                <Calendar size={64} style={{ color: '#9ca3af' }} />
                <h3 style={styles.emptyTitle}>No semesters found</h3>
                <p style={styles.emptySubtitle}>Add your first semester to begin</p>
              </div>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeadRow}>
                      <th style={styles.tableHead}>Semester No</th>
                      <th style={styles.tableHead}>Semester Name</th>
                      <th style={styles.tableHead}>Semester ID</th>
                      <th style={styles.tableHead}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSemesters.map((sem) => (
                      <tr key={sem.sem_id} style={styles.tableRow}>
                        <td style={styles.tableCell}>
                          <div style={styles.semNoChip}>{sem.sem_no}</div>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.semName}>{sem.sem_name}</div>
                        </td>
                        <td style={styles.tableCell}>
                          <code style={styles.semId}>{sem.sem_id}</code>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.actionButtons}>
                            <button
                              onClick={() => handleEdit(sem)}
                              style={styles.editBtn}
                              title="Edit"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(sem.sem_id)}
                              style={styles.deleteBtn}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────
// Styles (consistent with AcademicYearManagement)
const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px',
    background: '#f8fafc',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  contentWrapper: {
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  iconBg: {
    width: '56px',
    height: '56px',
    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerIcon: {
    color: 'white',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  subtitle: {
    color: '#64748b',
    fontSize: '16px',
    margin: 0,
  },
  newBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
  },
  card: {
    background: '#fff',
    borderRadius: '20px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    marginBottom: '32px',
  },
  cardHeader: {
    padding: '28px 32px',
    borderBottom: '1px solid #f1f5f9',
  },
  cardTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  message: {
    padding: '16px 24px',
    borderRadius: '12px',
    margin: '0 32px 24px 32px',
    fontWeight: '500',
  },
  successMsg: {
    background: 'linear-gradient(90deg, #dcfce7, #bbf7d0)',
    color: '#166534',
    border: '1px solid #86efac',
  },
  errorMsg: {
    background: 'linear-gradient(90deg, #fee2e2, #fecaca)',
    color: '#dc2626',
    border: '1px solid #fca5a5',
  },
  form: {
    padding: '32px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginBottom: '32px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    padding: '16px 20px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
  },
  formActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '10px 20px',
    background: '#f8fafc',
    color: '#64748b',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '15px',
  },
  submitBtnDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  searchContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    maxWidth: '300px',
  },
  searchIcon: {
    position: 'absolute',
    left: '16px',
    color: '#9ca3af',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '12px 16px 12px 48px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
  },
  tableContainer: {
    padding: '0',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeadRow: {
    background: '#f8fafc',
  },
  tableHead: {
    padding: '20px 24px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '700',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tableRow: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'all 0.2s ease',
  },
  tableCell: {
    padding: '20px 24px',
  },
  semNoChip: {
    width: '48px',
    height: '48px',
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    color: '#92400e',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '700',
  },
  semName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
  },
  semId: {
    background: '#f3f4f6',
    color: '#6b7280',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: 'monospace',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  editBtn: {
    padding: '10px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    border: 'none',
    cursor: 'pointer',
    color: '#1e40af',
    transition: 'all 0.2s ease',
  },
  deleteBtn: {
    padding: '10px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
    border: 'none',
    cursor: 'pointer',
    color: '#dc2626',
    transition: 'all 0.2s ease',
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 40px',
    color: '#6b7280',
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#374151',
    margin: '16px 0 8px 0',
  },
  emptySubtitle: {
    fontSize: '16px',
    margin: 0,
  },
  spinner: {
    width: 18,
    height: 18,
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Global animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  tr:hover { background: #f8fafc !important; }
`;
document.head.appendChild(styleSheet);

export default SemesterManagement;